import {
  ORDER_FEATURES,
  OrderFeature,
  TKV2_ORDER_ATTACHMENT_API,
  Tkv2OrderAttachment,
  ToolkitOrderID,
} from '@aloft/toolkit-shared';

import { Registry } from '../../../../../hooks/api/schema-hooks/registry.ts';
import {
  fetchGetAllCompData,
  fetchGetSubjectData,
  fetchGetTkv2OrderFeaturesAPI,
  usePutTkv2OrderFeaturesAPI,
} from '../../../../../hooks/api/toolkitComponents.ts';
import { useToolkitContext } from '../../../../../hooks/api/toolkitContext.ts';

export const useUpdateOrderFeatures = () => {
  const { getAllCompData, getSubjectData } = Registry;
  const getAllCompDataSchema = getAllCompData.schema;
  const getSubjectDataSchema = getSubjectData.schema;
  const orderFeaturesSchema = TKV2_ORDER_ATTACHMENT_API.ORDER_FEATURES_API.GET.RESPONSE;

  const { fetcherOptions } = useToolkitContext();
  const { mutate: orderFeaturesMutate } = usePutTkv2OrderFeaturesAPI();

  return async (orderID: ToolkitOrderID) => {
    try {
      const subjectDataPromise = fetchGetSubjectData({
        ...fetcherOptions,
        pathParams: { orderID },
      });
      const compDataPromise = fetchGetAllCompData({
        ...fetcherOptions,
        pathParams: { orderID },
      });
      const orderFeaturesPromise = fetchGetTkv2OrderFeaturesAPI({
        ...fetcherOptions,
        pathParams: { orderID },
      });

      const [subjectDataResponse, compDataResponse, orderFeaturesResponse] = await Promise.all([
        subjectDataPromise,
        compDataPromise,
        orderFeaturesPromise,
      ]);

      const compData = getAllCompDataSchema.parse(compDataResponse).comps;
      const subjectData = getSubjectDataSchema.parse(subjectDataResponse);
      const orderFeatures = orderFeaturesSchema.parse(
        orderFeaturesResponse as Tkv2OrderAttachment<'orderFeatures'>,
      ).body.features;

      const subjectImprovements = subjectData.editableData.improvements;
      const compsImprovements = Object.values(compData).map(
        (comp) => comp.editableData.improvements,
      );
      const improvements = [subjectImprovements, ...compsImprovements];

      const propertyFeatures = (Object.keys(ORDER_FEATURES) as OrderFeature[]).filter((feature) =>
        improvements.some((improvement) => ORDER_FEATURES[feature].hasFeature(improvement)),
      );

      const uniqueFeatures = Array.from(new Set(orderFeatures.concat(propertyFeatures)));

      return orderFeaturesMutate({
        pathParams: { orderID },
        body: TKV2_ORDER_ATTACHMENT_API.ORDER_FEATURES_API.PUT.REQUEST.BODY.parse({
          version: 1,
          features: uniqueFeatures,
        }),
      });
    } catch (e: unknown) {
      console.error('Failed to update order features', e);
      return undefined;
    }
  };
};
